/**
 * useRequestStatus Hook
 * Hook for polling request status by correlationId and fetching feature results
 */

import { useQuery, useQueries } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import { requestsService, RequestStatus, type RequestStatusResponse, type FeatureResult } from '@/services/api/requests';
import { env } from '@/utils/env';
import { getMockVertriebsgebietData } from '@/types/vertriebsgebiet';
import { getMockZielgruppeData } from '@/types/zielgruppe';
import { getMockProduktData } from '@/types/produkt';
import { getMockKommunikationData } from '@/types/kommunikation';
import { getMockMassnahmenData } from '@/types/massnahmen';
import { getMockHandlungsempfehlungenData } from '@/types/handlungsempfehlungen';
import { getMockSwotData } from '@/types/swot';
import { getMockZielgruppeB2BData } from '@/types/zielgruppeB2B';

/** Analysis type variants */
export type AnalysisVariant = 'v1' | 'v2';

export interface UseRequestStatusReturn {
  /** The request status data */
  data: RequestStatusResponse | undefined;
  /** Whether the initial fetch is loading */
  isLoading: boolean;
  /** Error from the fetch */
  error: Error | null;
  /** Whether the request is currently being polled (still processing) */
  isPolling: boolean;
  /** Whether the request is completed */
  isCompleted: boolean;
  /** Whether the request failed */
  isFailed: boolean;
  /** Progress percentage (0-100) */
  progress: number;
  /** Feature results with fetched data */
  features: FeatureResult[];
  /** Analysis variant (v1 or v2) determined by payload parameter */
  analysisVariant: AnalysisVariant;
  /** Refetch the status */
  refetch: () => void;
}

/** Polling interval in milliseconds (0.5 seconds) */
const POLLING_INTERVAL = 500;

/** Mock correlation ID for testing (dev only) */
export const MOCK_CORRELATION_ID = 'mock';

/** Mock correlation ID for V2 testing (dev only) */
export const MOCK_V2_CORRELATION_ID = 'mock-v2';

/** Mock correlation ID for loading state testing (dev only) */
export const MOCK_LOADING_CORRELATION_ID = 'mock-loading';

/** Use mock data in development only (never in production) */
const USE_MOCK_DATA = false;

/**
 * Creates mock request status data for testing/demo purposes
 */
function getMockRequestStatusData(): RequestStatusResponse {
  const now = new Date().toISOString();
  // Note: We stringify only the .main property because parse*Data() functions
  // expect raw data and wrap it in { main: ... } themselves
  const features: FeatureResult[] = [
    {
      featureId: 1,
      featureName: env.analysis.featureNames.vertriebsgebiet,
      featureDescription: 'Vertriebsgebiet Analysis',
      value: JSON.stringify(getMockVertriebsgebietData().main),
      status: RequestStatus.Completed,
      progressPercent: 100,
      totalPrompts: 1,
      completedPrompts: 1,
    },
    {
      featureId: 2,
      featureName: env.analysis.featureNames.zielgruppe,
      featureDescription: 'Zielgruppe Analysis',
      value: JSON.stringify(getMockZielgruppeData().main),
      status: RequestStatus.Completed,
      progressPercent: 100,
      totalPrompts: 1,
      completedPrompts: 1,
    },
    {
      featureId: 3,
      featureName: env.analysis.featureNames.produkt,
      featureDescription: 'Produkt Analysis',
      value: JSON.stringify(getMockProduktData().main),
      status: RequestStatus.Completed,
      progressPercent: 100,
      totalPrompts: 1,
      completedPrompts: 1,
    },
    {
      featureId: 4,
      featureName: env.analysis.featureNames.kommunikation,
      featureDescription: 'Kommunikation Analysis',
      value: JSON.stringify(getMockKommunikationData().main),
      status: RequestStatus.Completed,
      progressPercent: 100,
      totalPrompts: 1,
      completedPrompts: 1,
    },
    {
      featureId: 5,
      featureName: env.analysis.featureNames.massnahmen,
      featureDescription: 'Massnahmen Analysis',
      value: JSON.stringify(getMockMassnahmenData().main),
      status: RequestStatus.Completed,
      progressPercent: 100,
      totalPrompts: 1,
      completedPrompts: 1,
    },
    {
      featureId: 6,
      featureName: env.analysis.featureNames.handlungsempfehlungen,
      featureDescription: 'Handlungsempfehlungen Analysis',
      value: JSON.stringify(getMockHandlungsempfehlungenData().main),
      status: RequestStatus.Completed,
      progressPercent: 100,
      totalPrompts: 1,
      completedPrompts: 1,
    },
  ];

  return {
    correlationId: MOCK_CORRELATION_ID,
    productId: env.analysis.productIdByZielgruppe.privatkunden,
    status: RequestStatus.Completed,
    progressPercent: 100,
    createdAt: now,
    finishedAt: now,
    features,
  };
}

/**
 * Creates mock request status data for V2 (Geschäftskunden) testing/demo purposes
 */
function getMockRequestStatusDataV2(): RequestStatusResponse {
  const now = new Date().toISOString();
  const features: FeatureResult[] = [
    {
      featureId: 1,
      featureName: env.analysis.featureNames.swot,
      featureDescription: 'SWOT Analysis',
      value: JSON.stringify(getMockSwotData().main),
      status: RequestStatus.Completed,
      progressPercent: 100,
      totalPrompts: 1,
      completedPrompts: 1,
    },
    {
      featureId: 2,
      featureName: env.analysis.featureNames.zielgruppeB2B,
      featureDescription: 'B2B Zielgruppe Analysis',
      value: JSON.stringify(getMockZielgruppeB2BData().main),
      status: RequestStatus.Completed,
      progressPercent: 100,
      totalPrompts: 1,
      completedPrompts: 1,
    },
    {
      featureId: 3,
      featureName: env.analysis.featureNames.zielgruppe,
      featureDescription: 'Zielgruppe Analysis',
      value: JSON.stringify(getMockZielgruppeData().main),
      status: RequestStatus.Completed,
      progressPercent: 100,
      totalPrompts: 1,
      completedPrompts: 1,
    },
    {
      featureId: 4,
      featureName: env.analysis.featureNames.produkt,
      featureDescription: 'Produkt Analysis',
      value: JSON.stringify(getMockProduktData().main),
      status: RequestStatus.Completed,
      progressPercent: 100,
      totalPrompts: 1,
      completedPrompts: 1,
    },
    {
      featureId: 5,
      featureName: env.analysis.featureNames.kommunikation,
      featureDescription: 'Kommunikation Analysis',
      value: JSON.stringify(getMockKommunikationData().main),
      status: RequestStatus.Completed,
      progressPercent: 100,
      totalPrompts: 1,
      completedPrompts: 1,
    },
    {
      featureId: 6,
      featureName: env.analysis.featureNames.massnahmen,
      featureDescription: 'Massnahmen Analysis',
      value: JSON.stringify(getMockMassnahmenData().main),
      status: RequestStatus.Completed,
      progressPercent: 100,
      totalPrompts: 1,
      completedPrompts: 1,
    },
    {
      featureId: 7,
      featureName: env.analysis.featureNames.handlungsempfehlungen,
      featureDescription: 'Handlungsempfehlungen Analysis',
      value: JSON.stringify(getMockHandlungsempfehlungenData().main),
      status: RequestStatus.Completed,
      progressPercent: 100,
      totalPrompts: 1,
      completedPrompts: 1,
    },
  ];

  return {
    correlationId: MOCK_V2_CORRELATION_ID,
    productId: env.analysis.productIdByZielgruppe.geschaeftskunden,
    status: RequestStatus.Completed,
    progressPercent: 100,
    createdAt: now,
    finishedAt: now,
    features,
  };
}

/** Start time for mock-loading progress simulation */
let mockLoadingStartTime = 0;

/**
 * Creates mock request status data that simulates an in-progress analysis.
 * Progress slowly increments over ~3 minutes, resetting when it reaches 95%.
 */
function getMockLoadingRequestStatusData(): RequestStatusResponse {
  const now = Date.now();
  if (mockLoadingStartTime === 0) mockLoadingStartTime = now;

  // Progress increases ~0.5% per second, resets at 95%
  const elapsed = (now - mockLoadingStartTime) / 1000;
  const rawProgress = (elapsed * 0.5) % 95;
  const progressPercent = Math.round(rawProgress);

  const featureNames = [
    env.analysis.featureNames.vertriebsgebiet,
    env.analysis.featureNames.zielgruppe,
    env.analysis.featureNames.produkt,
    env.analysis.featureNames.kommunikation,
    env.analysis.featureNames.massnahmen,
    env.analysis.featureNames.handlungsempfehlungen,
  ];

  const features: FeatureResult[] = featureNames.map((name, i) => ({
    featureId: i + 1,
    featureName: name,
    value: '',
    status: RequestStatus.InProgress,
    progressPercent: Math.min(100, Math.round(rawProgress * (1 + i * 0.1))),
    totalPrompts: 3,
    completedPrompts: 0,
  }));

  return {
    correlationId: MOCK_LOADING_CORRELATION_ID,
    productId: env.analysis.productIdByZielgruppe.privatkunden,
    status: RequestStatus.InProgress,
    progressPercent,
    createdAt: new Date(mockLoadingStartTime).toISOString(),
    features,
  };
}

/**
 * Hook that fetches request status and polls for updates while processing
 * When a feature is completed (status 2), it fetches the feature result
 * @param correlationId - The correlation ID of the request
 */
export function useRequestStatus(correlationId: string): UseRequestStatusReturn {
  // Mock mode is only available in development, never in production
  const isMockRequest = !env.isProd && (USE_MOCK_DATA || correlationId === MOCK_CORRELATION_ID || correlationId === MOCK_V2_CORRELATION_ID || correlationId === MOCK_LOADING_CORRELATION_ID);
  const isMockLoading = !env.isProd && correlationId === MOCK_LOADING_CORRELATION_ID;

  // Track fetched feature results to merge with status data
  const fetchedResultsRef = useRef<Map<string, string>>(new Map());

  const {
    data: statusData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['requestStatus', correlationId, USE_MOCK_DATA],
    queryFn: () => {
      if (isMockLoading) {
        return getMockLoadingRequestStatusData();
      }
      if (isMockRequest) {
        return correlationId === MOCK_V2_CORRELATION_ID
          ? getMockRequestStatusDataV2()
          : getMockRequestStatusData();
      }
      return requestsService.getRequestStatus(correlationId);
    },
    enabled: !!correlationId,
    // Enable polling only while processing (never for mock, except mock-loading)
    refetchInterval: (query) => {
      if (isMockLoading) return POLLING_INTERVAL;
      if (isMockRequest) return false;
      const responseData = query.state.data as RequestStatusResponse | undefined;
      if (
        responseData?.status === RequestStatus.Pending ||
        responseData?.status === RequestStatus.InProgress
      ) {
        return POLLING_INTERVAL;
      }
      return false;
    },
  });

  // Get list of completed features that need result fetching (skip for mock data)
  const completedFeatures = useMemo(() => {
    if (isMockRequest || !statusData?.features) return [];
    return statusData.features
      .filter(f => f.status === RequestStatus.Completed && !f.value && !fetchedResultsRef.current.has(f.featureName))
      .map(f => f.featureName);
  }, [isMockRequest, statusData?.features]);

  // Fetch results for completed features
  const featureResultQueries = useQueries({
    queries: completedFeatures.map(featureName => ({
      queryKey: ['featureResult', correlationId, featureName],
      queryFn: async () => {
        const result = await requestsService.getFeatureResult(correlationId, featureName);
        // Convert result to JSON string and cache it
        const resultValue = JSON.stringify(result.result);
        fetchedResultsRef.current.set(featureName, resultValue);
        return { featureName, value: resultValue };
      },
      enabled: !!correlationId && completedFeatures.includes(featureName),
      staleTime: Infinity, // Don't refetch once we have the result
      retry: 2,
    })),
  });

  // Merge fetched results into features
  const features = useMemo(() => {
    if (!statusData?.features) return [];

    // Build a map of fetched results from queries
    const queryResults = new Map<string, string>();
    featureResultQueries.forEach(query => {
      if (query.data) {
        queryResults.set(query.data.featureName, query.data.value);
      }
    });

    // Merge with cached results
    const allResults = new Map([...fetchedResultsRef.current, ...queryResults]);

    return statusData.features.map(feature => {
      const fetchedValue = allResults.get(feature.featureName);
      if (fetchedValue && !feature.value) {
        return { ...feature, value: fetchedValue };
      }
      return feature;
    });
  }, [statusData?.features, featureResultQueries]);

  // Determine if we're currently polling
  const isPolling = useMemo(() => {
    return statusData?.status === RequestStatus.Pending || statusData?.status === RequestStatus.InProgress;
  }, [statusData?.status]);

  // Check if completed
  const isCompleted = useMemo(() => {
    return statusData?.status === RequestStatus.Completed;
  }, [statusData?.status]);

  // Check if failed
  const isFailed = useMemo(() => {
    return statusData?.status === RequestStatus.Failed;
  }, [statusData?.status]);

  // Progress
  const progress = useMemo(() => {
    return statusData?.progressPercent ?? 0;
  }, [statusData?.progressPercent]);

  // Determine analysis variant based on productId
  const analysisVariant = useMemo((): AnalysisVariant => {
    if (!statusData?.productId) return 'v1';

    const variantConfig = env.analysis.analysisVariant;

    // Check if productId is in V2 list
    if (variantConfig.v2ProductIds.includes(statusData.productId)) {
      return 'v2';
    }
    return 'v1';
  }, [statusData?.productId]);

  return {
    data: statusData,
    isLoading,
    error: error as Error | null,
    isPolling,
    isCompleted,
    isFailed,
    progress,
    features,
    analysisVariant,
    refetch,
  };
}

export default useRequestStatus;
